import { useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TEMPLATES_PATH } from 'routes/routes.constants';

import useMatchPath from '@polyai/common/hooks/useMatchPath';
import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import api from 'api';
import { setVersionState } from 'utils/versionState';

// Track at module level so all instances of the hook share the same state
let lastSampleProjectId: string | null = null;

const useSampleProject = (enabled: boolean = true) => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();

  const queryClient = useQueryClient();

  const matchPath = useMatchPath();
  const inSampleProject = useMemo(
    () => !!matchPath(TEMPLATES_PATH, false),
    [matchPath],
  );

  const {
    data: sampleProject,
    isLoading: isLoadingSampleProject,
    isError: didSampleProjectError,
  } = useQuery(
    [projectId, 'sample-project'],
    () => api.getSampleProject(projectId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: enabled && inSampleProject && !!projectId,
    },
  );

  useEffect(() => {
    // Only set version state once per sample project to prevent infinite loops
    const sampleProjectId = projectId;
    if (
      sampleProject &&
      sampleProject.config &&
      sampleProjectId !== lastSampleProjectId
    ) {
      lastSampleProjectId = sampleProjectId;

      setVersionState({
        accountId,
        projectId,
        queryClient,
        versionStateData: sampleProject,
        mockData: true,
      });
    }
  }, [sampleProject, accountId, projectId, queryClient]);

  return useMemo(
    () => ({
      sampleProject,
      isLoadingSampleProject,
      didSampleProjectError,
      inSampleProject,
    }),
    [
      sampleProject,
      isLoadingSampleProject,
      didSampleProjectError,
      inSampleProject,
    ],
  );
};

export default useSampleProject;
